import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const LoginOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 2rem;
`;

const LoginCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 3rem;
  max-width: 450px;
  width: 100%;
  border: 1px solid #333333;
  box-shadow: 0 20px 60px rgba(212, 175, 55, 0.1);
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #d4af37;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #cfcfcf;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.875rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 1rem;
  border: 1px solid #333333;
  border-radius: 8px;
  background: #0d0d0d;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const Button = styled(motion.button)`
  padding: 1rem;
  background: #d4af37;
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  text-align: center;
`;

const SuccessMessage = styled(motion.div)`
  background: #e8f5e8;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  text-align: center;
`;

const InfoBox = styled.div`
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid #d4af37;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const InfoText = styled.p`
  color: #d4af37;
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid #333333;
  color: #cfcfcf;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    color: #d4af37;
  }
`;

interface AdminLoginProps {
  onLoginSuccess: (token: string) => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [step, setStep] = useState<'customer_id' | 'password'>('customer_id');
  const [customerId, setCustomerId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Orders sayfasından gelen admin customer ID'yi kontrol et
  React.useEffect(() => {
    const savedCustomerId = localStorage.getItem('admin_customer_id');
    if (savedCustomerId) {
      setCustomerId(savedCustomerId);
      // Direkt şifre adımına geç - zaten Orders sayfasında kontrol edildi
      setStep('password');
    }
  }, []);

  const handleCustomerIdCheck = async (customerIdToCheck: string) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/check-customer-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_id: customerIdToCheck }),
      });

      const data = await response.json();

      if (data.success && data.is_admin) {
        setSuccess('Müşteri kimlik numarası doğrulandı. Lütfen şifrenizi girin.');
        setTimeout(() => {
          setStep('password');
          setSuccess('');
        }, 500);
      } else {
        setError('Bu müşteri kimlik numarası admin paneline erişim yetkisine sahip değil.');
        localStorage.removeItem('admin_customer_id');
      }
    } catch (err: any) {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
      localStorage.removeItem('admin_customer_id');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleCustomerIdCheck(customerId);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Giriş başarılı! Yönlendiriliyorsunuz...');
        localStorage.setItem('admin_token', data.session_token);
        localStorage.setItem('admin_expires_at', data.expires_at);
        setTimeout(() => {
          onLoginSuccess(data.session_token);
        }, 1000);
      } else {
        setError(data.error || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
      }
    } catch (err: any) {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('customer_id');
    setPassword('');
    setError('');
    setSuccess('');
    localStorage.removeItem('admin_customer_id');
  };

  return (
    <LoginOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <LoginCard
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 20 }}
      >
        <Title>🔐 Admin Paneli</Title>
        <Subtitle>
          {step === 'customer_id' 
            ? 'Müşteri kimlik numaranızı girin'
            : 'Şifrenizi girin'}
        </Subtitle>

        <AnimatePresence mode="wait">
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {error}
            </ErrorMessage>
          )}
          {success && (
            <SuccessMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {success}
            </SuccessMessage>
          )}
        </AnimatePresence>

        {step === 'customer_id' ? (
          <Form onSubmit={handleCustomerIdSubmit}>
            <InputGroup>
              <Label>Müşteri Kimlik Numarası</Label>
              <Input
                type="text"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="Örn: ADMIN-2024-SECURE-9876543210"
                required
                disabled={loading}
                autoFocus
              />
            </InputGroup>

            <Button
              type="submit"
              disabled={loading || !customerId}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Kontrol Ediliyor...' : 'Devam Et'}
            </Button>

            <InfoBox>
              <InfoText>
                ℹ️ Admin paneline erişim için özel müşteri kimlik numaranızı kullanın.
              </InfoText>
            </InfoBox>
          </Form>
        ) : (
          <Form onSubmit={handlePasswordSubmit}>
            <InputGroup>
              <Label>Şifre</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin şifrenizi girin"
                required
                disabled={loading}
                autoFocus
              />
            </InputGroup>

            <Button
              type="submit"
              disabled={loading || !password}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
            </Button>

            <BackButton type="button" onClick={handleBack} disabled={loading}>
              ← Geri Dön
            </BackButton>

            <InfoBox>
              <InfoText>
                🔒 Güvenli bağlantı ile korunuyorsunuz
              </InfoText>
            </InfoBox>
          </Form>
        )}
      </LoginCard>
    </LoginOverlay>
  );
};

export default AdminLogin;
